import { A } from '@ember/array';
import { action } from '@ember/object';
import { htmlSafe } from '@ember/template';
import type { SafeString } from '@ember/template/-private/handlebars';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { dropTask, timeout } from 'ember-concurrency';
import type AverageMonthlyVisitorCountModel from 'garaje/models/average-monthly-visitor-count';
import type LocationModel from 'garaje/models/location';
import type { ModelData } from 'garaje/utils/type-utils';

const SORT_KEYS = {
  'Location name': 'name',
  'Location created': 'createdAt',
  'Last sign-in': 'lastEntryAt',
  'Average monthly visitors': 'averageMonthlyVisitorCount',
} as const;

interface LocationsTableComponentSignature {
  Args: {
    locations: LocationModel[];
    averageMonthlyVisitorCount: AverageMonthlyVisitorCountModel[];
  };
}

type LocationWithAverageCount = ModelData<LocationModel> & {
  averageMonthlyVisitorCount: number;
};

export default class LocationsTableComponent extends Component<LocationsTableComponentSignature> {
  @tracked sortBy: keyof typeof SORT_KEYS = 'Location name';
  @tracked sortDirection = 'A-Z';
  @tracked fixHeader = false;
  @tracked selectedLocations: LocationModel[] = [];
  @tracked resizeListener;
  @tracked scrollListener;
  @tracked declare element: HTMLDivElement;

  constructor(owner: unknown, args: LocationsTableComponentSignature['Args']) {
    super(owner, args);
    this.selectedLocations = [];

    this.resizeListener = () => {
      void this.resizeColsTask.perform();
    };

    this.scrollListener = () => {
      void this.adjustHeaderStylesTask.perform();
    };

    window.addEventListener('resize', this.resizeListener);
    window.addEventListener('scroll', this.scrollListener);
  }

  get locationsWithAverageCount(): LocationWithAverageCount[] {
    const locations = this.args.locations;
    const averageMonthlyVisitorCounts = this.args.averageMonthlyVisitorCount;

    return locations.map((location) => {
      const countObject: Partial<AverageMonthlyVisitorCountModel> =
        averageMonthlyVisitorCounts.find((a) => {
          return a.belongsTo('location').id() === location.id;
        }) || {};

      return {
        ...location.toJSON(),
        averageMonthlyVisitorCount: countObject.count || 0,
      };
    });
  }

  get headerStyles(): SafeString {
    let style = ``;

    if (this.fixHeader) {
      style = `
          position: fixed;
          top: 0;
          box-shadow: 0 6px 12px 0 #e9e9ea;
        `;
    }

    return htmlSafe(style);
  }

  get isSortAsc(): boolean {
    const sortAscKeys = ['A-Z', 'Least to most recent', 'Oldest to newest', 'Fewest to most'];
    return sortAscKeys.includes(this.sortDirection);
  }

  get sortedLocations(): LocationWithAverageCount[] {
    const sortBy = SORT_KEYS[this.sortBy];
    let locations = A(this.locationsWithAverageCount).sortBy(sortBy).slice();

    if (!this.isSortAsc) {
      locations = locations.reverse();
    }

    return locations;
  }

  @action
  onInsert(element: HTMLDivElement): void {
    this.element = element;
    void this.resizeColsTask.perform();
  }

  willDestroy(...args: Parameters<typeof Component.prototype.willDestroy>): void {
    super.willDestroy(...args);
    window.removeEventListener('resize', this.resizeListener);
    window.removeEventListener('scroll', this.scrollListener);
  }

  /*
   * resizeColsTask
   *
   * In order to acheive the floating cols as the talble scrolls, we
   * have a dispaly table ([data-display-table]) and hide actual
   * `thead`. This task syncs with column width between the display
   * table and actual table to simulate the native content stretch
   * that occurs in an HTML table.
   */
  resizeColsTask = dropTask(async () => {
    await timeout(100);
    const tableCols = this.element.querySelectorAll('[data-display-table] tr:first-child td');
    const tableColWidths = [...tableCols].map((col) => col.clientWidth);
    const tableThs = this.element.querySelectorAll<HTMLTableCellElement>('[data-header-table] th');

    tableThs.forEach((col, index) => {
      col.style.width = `${tableColWidths[index]}px`;
    });
  });

  adjustHeaderStylesTask = dropTask(async () => {
    await timeout(10);

    if (
      document.querySelector('html')!.scrollTop > document.querySelector<HTMLDivElement>('.contentContainer')!.offsetTop
    ) {
      this.fixHeader = true;
    } else {
      this.fixHeader = false;
    }
  });

  @action
  toggleSelectLocation(location: LocationModel): void {
    const locations = this.selectedLocations;
    const locationId = location.id;

    if (locations.find((loc) => loc.id === locationId)) {
      const indexOfElementToRemove = locations.findIndex((location) => location.id === locationId);
      this.selectedLocations = [
        ...locations.slice(0, indexOfElementToRemove),
        ...locations.slice(indexOfElementToRemove + 1),
      ];
    } else {
      this.selectedLocations = [...locations, location];
    }
  }

  @action
  toggleAllLocations(): void {
    const selectedLocations = this.selectedLocations;
    const locations = this.args.locations;

    if (selectedLocations.length === locations.length) {
      this.selectedLocations = [];
    } else {
      this.selectedLocations = [...locations];
    }
  }
}
